import {Transition} from '@headlessui/react';
import React, {Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {ArrowNarrowDownIcon, ArrowNarrowUpIcon} from "@heroicons/react/outline";
import {useTranslation} from "react-i18next";
import NumberFormat from "react-number-format";
import {createPortal} from "react-dom";

export default function PriceRange({
                                       price,
                                       purchasePrice,
                                       isLeft = false,
                                       showMargin = false,
                                       showMarginTotals = true,
                                       boxExtraClass = '',
                                       symbol = '€',
                                       extraCosts = 0,
                                       extraCostsPercentage = 0,
                                       extra_costs = [],
                                       product_extra_costs = [],
                                       quotation_extra_costs = [],
                                       margin = 0,
                                       extraCostValue = false,
                                       quantity = 0
                                   }) {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState(null);
    const effectiveMargin =
        typeof margin === 'number' && margin > 0
            ? margin
            : ((price - (purchasePrice + extra_costs)) / price) * 100 - extraCostsPercentage;

    const updateDropdownPosition = useCallback(() => {
        if (typeof window === "undefined" || !containerRef.current) {
            return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY,
            width: rect.width,
        });
    }, []);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        setDropdownPosition(null);
    }, []);

    const toggleDropdown = useCallback(() => {
        setIsOpen((previous) => {
            const next = !previous;
            if (next && typeof window !== "undefined") {
                requestAnimationFrame(updateDropdownPosition);
            }
            if (!next) {
                setDropdownPosition(null);
            }
            return next;
        });
    }, [updateDropdownPosition]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleClickOutside = (event) => {
            if (containerRef.current && containerRef.current.contains(event.target)) {
                return;
            }
            if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
                return;
            }
            closeDropdown();
        };

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeDropdown();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [closeDropdown, isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        updateDropdownPosition();
        const handleRelayout = () => {
            updateDropdownPosition();
        };
        if (typeof window !== "undefined") {
            window.addEventListener("resize", handleRelayout);
            window.addEventListener("scroll", handleRelayout, true);
        }
        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", handleRelayout);
                window.removeEventListener("scroll", handleRelayout, true);
            }
        };
    }, [isOpen, updateDropdownPosition]);

    if(!showMargin){
        return  (<></>)
    }


    //const colorClass = effectiveMargin > 0 ? 'text-green-900' : 'text-red-900'
    return (
        <>
            <div ref={containerRef} className="inline-flex">
                <button
                    type="button"
                    onClick={toggleDropdown}
                    aria-expanded={isOpen}
                    className="flex items-center"
                >
                    {(purchasePrice && (price > 0)) ? (
                        <div className="flex items-center ">

                            {
                                showMarginTotals && !isLeft && price > 0 && (
                                    <div className={price > purchasePrice ? "text-green-900" : "text-red-900"}>
                                        <b><NumberFormat
                                            value={effectiveMargin}
                                            displayType={"text"}
                                            thousandSeparator={"."}
                                            decimalSeparator={","}
                                            decimalScale={2}
                                            suffix={'%'}
                                        /></b>
                                    </div>
                                )
                            }

                            {
                                price > purchasePrice && price > 0 ? (
                                    <ArrowNarrowUpIcon
                                        className={isLeft ? "cursor-pointer text-green-400 h-5 w-5 ml-3" : "cursor-pointer text-green-400 h-5 w-5 mr-3"}/>
                                ) : (
                                    <ArrowNarrowDownIcon
                                        className={isLeft ? "cursor-pointer text-red-400 h-5 w-5 ml-3" : "cursor-pointer text-red-400 h-5 w-5 mr-3"}/>
                                )
                            }

                            {
                                showMarginTotals && isLeft && price > 0 && (
                                    <div className={price > purchasePrice ? "text-green-900" : "text-red-900"}>

                                        <b>
                                            <NumberFormat
                                                value={effectiveMargin}
                                                displayType="text"
                                                thousandSeparator="."
                                                decimalSeparator=","
                                                decimalScale={2}
                                                suffix={'%'}
                                            />
                                        </b>
                                    </div>
                                )
                            }

                        </div>
                    ) : null}
                </button>
            </div>

            {isOpen && dropdownPosition && typeof document !== "undefined"
                ? createPortal(
                    <Transition
                        show={isOpen}
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                    >
                        <div
                            ref={dropdownRef}
                            className={`z-[1000] mb-1 max-w-md overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg ${boxExtraClass}`}
                            style={{
                                position: "absolute",
                                left: dropdownPosition.left - 100,
                                top: dropdownPosition.top - 4,
                                width: typeof dropdownPosition.width === "number"
                                    ? Math.max(dropdownPosition.width, 200)
                                    : dropdownPosition.width,
                                transform: "translateY(-100%)",
                            }}
                        >
                            <nav aria-label="Directory">
                                <ul className="divide-y divide-gray-100 text-sm font-medium text-gray-900">
                                    <li className=" px-2 bg-white">
                                        <div className="py-2 flex items-center justify-between space-x-3">
                                            <div className="min-w-0 flex-1 flex items-center space-x-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-600 truncate">
                                                        {t("app.quotations.purchase_price")}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mt-4 space-x-3 flex md:mt-0 md:ml-4">
                                                <NumberFormat
                                                    value={purchasePrice}
                                                    displayType={"text"}
                                                    thousandSeparator={"."}
                                                    decimalSeparator={","}
                                                    decimalScale={2}
                                                    suffix={' ' + symbol}
                                                />
                                            </div>
                                        </div>
                                    </li>

                                    <li className=" px-2 bg-white">
                                        <div className="py-2 flex items-center justify-between space-x-3">
                                            <div className="min-w-0 flex-1 flex items-center space-x-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-600 truncate">
                                                        {t("app.quotations.net_price")}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mt-4 space-x-3 flex md:mt-0 md:ml-4">
                                                <NumberFormat
                                                    value={price}
                                                    displayType={"text"}
                                                    thousandSeparator={"."}
                                                    decimalSeparator={","}
                                                    decimalScale={2}
                                                    suffix={' ' + symbol}
                                                />
                                            </div>
                                        </div>
                                    </li>

                                    {Array.isArray(quotation_extra_costs) && quotation_extra_costs.length > 0 && quotation_extra_costs.map((extra, idx) => (
                                        <li key={idx} className="px-2 bg-white">
                                            <div className="py-2 flex items-center justify-between space-x-3">
                                                <div className="min-w-0 flex-1 flex items-center space-x-3">
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-600 truncate">
                                                            {extra.condition.condition_name} {extra.condition.type}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="mt-4 space-x-3 flex md:mt-0 md:ml-4">
                                                    <NumberFormat
                                                        value={extra.condition.cost}
                                                        displayType={"text"}
                                                        thousandSeparator={"."}
                                                        decimalSeparator={","}
                                                        decimalScale={2}
                                                        suffix={
                                                            extra.condition.price_type === 'fixed'
                                                                ? ' ' + symbol
                                                                : ' %'
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </li>
                                    ))}

                                    {Array.isArray(product_extra_costs) && product_extra_costs.length > 0 && product_extra_costs.map((cost, idx) => (
                                        <li key={idx} className="px-2 bg-white">
                                            <div className="py-2 flex items-center justify-between space-x-3">
                                                <div className="min-w-0 flex-1 flex items-center space-x-3">
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-600 truncate">
                                                            {cost.name} -  <small>{cost.price_type} </small>
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="mt-4 space-x-3 flex md:mt-0 md:ml-4">
                                                    <NumberFormat
                                                        value={cost.cost}
                                                        displayType={"text"}
                                                        thousandSeparator={"."}
                                                        decimalSeparator={","}
                                                        decimalScale={2}
                                                        suffix={
                                                            cost.price_type === 'fixed'
                                                                ? ' ' + symbol
                                                                : ' %'
                                                        }

                                                    />
                                                </div>
                                            </div>
                                        </li>
                                    ))}

                                    {extraCostValue > 0 ? (
                                        <li className=" px-2 bg-solitude-200">
                                            <div className="py-2 flex items-center justify-between space-x-3">
                                                <div className="min-w-0 flex-1 flex items-center space-x-3">
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-600 truncate">
                                                            {t("app.quotations.extra_costs")} ({symbol})
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="mt-4 space-x-3 flex md:mt-0 md:ml-4">
                                                    <NumberFormat
                                                        value={extraCostValue}
                                                        displayType={"text"}
                                                        thousandSeparator={"."}
                                                        decimalSeparator={","}
                                                        decimalScale={2}
                                                        suffix={' ' + symbol}
                                                    />
                                                </div>
                                            </div>
                                        </li>
                                    ) : null}

                                    <li className=" px-2 bg-solitude-100">
                                        <div className="py-2 flex items-center justify-between space-x-3">
                                            <div className="min-w-0 flex-1 flex items-center space-x-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-600 truncate">
                                                        {t("app.quotations.margin")} ({symbol})
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mt-4 space-x-3 flex md:mt-0 md:ml-4">
                                                <NumberFormat
                                                    value={(price - (purchasePrice + extraCosts)) - ((price * extraCostsPercentage) / 100) - extraCostValue}
                                                    displayType={"text"}
                                                    thousandSeparator={"."}
                                                    decimalSeparator={","}
                                                    decimalScale={2}
                                                    suffix={' ' + symbol}
                                                />
                                            </div>
                                        </div>
                                    </li>

                                    <li className=" px-2 bg-solitude-100">
                                        <div className="py-2 flex items-center justify-between space-x-3">
                                            <div className="min-w-0 flex-1 flex items-center space-x-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {t("app.quotations.margin")} (%)
                                                    </p>

                                                </div>
                                            </div>
                                            <div className={price > purchasePrice ? "mt-4 space-x-3 flex md:mt-0 md:ml-4 text-green-900" : "mt-4 space-x-3 flex md:mt-0 md:ml-4 text-red-900"}>
                                                {margin > 0 ? (
                                                    <b><NumberFormat
                                                        value={margin}
                                                        displayType={"text"}
                                                        thousandSeparator={"."}
                                                        decimalSeparator={","}
                                                        decimalScale={2}
                                                        suffix={' %'}
                                                    /></b>

                                                ) : (
                                                    <b><NumberFormat
                                                        value={((price - (purchasePrice + extraCostValue)) / price * 100)}
                                                        displayType={"text"}
                                                        thousandSeparator={"."}
                                                        decimalSeparator={","}
                                                        decimalScale={2}
                                                        suffix={' %'}
                                                    /></b>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </Transition>,
                    document.body
                )
                : null}
        </>
    )
}
